import React, { useEffect, useState } from 'react';

import _ from 'lodash';
import { ExpandCollapse, Typography } from '@telus-uds/components-web';

import { useLocalizedStringsContext } from '../../../../context/LocalizedStringsContext';
import { Box, Flex } from './css';
import { getFormattedDate } from '../../../../utils/utils';
import request, { isOrderFulfillmentTeam } from '../../../../utils/request';
import TypeAhead from '../../../../orderDetails/components/typeAhead';
import BarChart from './barChart';
import Table from '../../../../design/shared/table';

const Analytics = () => {
  if (!isOrderFulfillmentTeam) return null;

  const localizedStrings = useLocalizedStringsContext();
  const {
    ORDERS: { orderId, lastCaseComment },
    general: { loading },
    srtDashboard: {
      status: {
        pendingValidation,
        waitingOnSales,
        waitingOnCredit,
        contractSent,
        contractSigned,
        orderPlaced,
        orderActivated,
        orderShipped,
        orderCompleted,
        orderCanceled,
        contractExpired
      }
    },
    analytics: {
      analytics,
      selectTheView,
      total,
      oldestOrder,
      orderStatus,
      noOfDays,
      timestamp,
      totalCount
    }
  } = localizedStrings;
  const initOptions = [
    {
      label: '',
      id: ''
    },
    {
      label: total,
      id: total
    }
  ];
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState(initOptions);
  const [statsBarChart, setStatsBarChart] = useState();
  const [statsTable, setStatsTable] = useState();
  const [selectedOption, setSelectedOption] = useState();

  const bars = [
    {
      dataKey: pendingValidation,
      color: '#0A84A5'
    },
    {
      dataKey: waitingOnSales,
      color: '#6F4D7C'
    },
    {
      dataKey: waitingOnCredit,
      color: '#F6C85F'
    },

    {
      dataKey: contractSent,
      color: '#9ED866'
    },
    {
      dataKey: contractSigned,
      color: '#CA472F'
    },
    {
      dataKey: orderPlaced,
      color: '#5D9CEC'
    },
    {
      dataKey: orderActivated,
      color: '#48CFAD'
    },
    {
      dataKey: orderShipped,
      color: '#55CE54'
    },
    {
      dataKey: orderCompleted,
      color: '#FC6E51'
    },
    {
      dataKey: orderCanceled,
      color: '#1B96C6'
    },
    {
      dataKey: contractExpired,
      color: '#FF9E9A'
    }
  ];

  const tableHeader = {
    oldestOrder,
    orderId,
    timestamp,
    noOfDays,
    lastCaseComment
  };

  const getStats = userId => {
    if (!userId) {
      setStatsBarChart(null);
      setStatsTable(null);
      setSelectedOption(null);
      return;
    }

    let url = '/orders/stats';
    if (userId && userId !== total) {
      url = url.concat('?type=IOT&assigned_user_id=', userId);
    }
    setIsLoading(true);
    request
      .get(url)
      .then(response => response.data)
      .then(data => {
        const {
          STATUS_COUNT,
          OLDEST_STATUS_ORDER: {
            PENDING_VALIDATION,
            WAITING_ON_CREDIT,
            WAITING_ON_SALES,
            CONTRACT_SENT,
            WITH_CONCIERGE,
            CONTRACT_EXPIRED
          }
        } = data;
        const newStatsBarChart = [
          {
            name: orderStatus,
            [pendingValidation]: STATUS_COUNT.PENDING_VALIDATION,
            [waitingOnCredit]: STATUS_COUNT.WAITING_ON_CREDIT,
            [waitingOnSales]: STATUS_COUNT.WAITING_ON_SALES,
            [contractSent]: STATUS_COUNT.CONTRACT_SENT,
            [contractSigned]: _.get(STATUS_COUNT, 'CONTRACT_SIGNED', 0),
            [orderPlaced]: _.get(STATUS_COUNT, 'ORDER_PLACED', 0),
            [orderActivated]: _.get(STATUS_COUNT, 'ORDER_ACTIVATED', 0),
            [orderShipped]: _.get(STATUS_COUNT, 'ORDER_SHIPPED', 0),
            [orderCompleted]: _.get(STATUS_COUNT, 'ORDER_COMPLETED', 0),
            [orderCanceled]: _.get(STATUS_COUNT, 'ORDER_CANCELED', 0),
            [contractExpired]: STATUS_COUNT.CONTRACT_EXPIRED
          }
        ];
        const newStatsTable = [
          {
            oldestOrder: pendingValidation,
            orderId: PENDING_VALIDATION.order_id,
            timestamp: getFormattedDate(PENDING_VALIDATION.status_changed_at),
            noOfDays: PENDING_VALIDATION.number_of_days,
            lastCaseComment: PENDING_VALIDATION.last_comment
          },
          {
            oldestOrder: waitingOnCredit,
            orderId: WAITING_ON_CREDIT.order_id,
            timestamp: getFormattedDate(WAITING_ON_CREDIT.status_changed_at),
            noOfDays: WAITING_ON_CREDIT.number_of_days,
            lastCaseComment: WAITING_ON_CREDIT.last_comment
          },
          {
            oldestOrder: waitingOnSales,
            orderId: WAITING_ON_SALES.order_id,
            timestamp: getFormattedDate(WAITING_ON_SALES.status_changed_at),
            noOfDays: WAITING_ON_SALES.number_of_days,
            lastCaseComment: WAITING_ON_SALES.last_comment
          },
          {
            oldestOrder: contractSent,
            orderId: CONTRACT_SENT.order_id,
            timestamp: getFormattedDate(CONTRACT_SENT.status_changed_at),
            noOfDays: CONTRACT_SENT.number_of_days,
            lastCaseComment: CONTRACT_SENT.last_comment
          },
          {
            oldestOrder: contractSigned,
            orderId: WITH_CONCIERGE.order_id,
            timestamp: getFormattedDate(WITH_CONCIERGE.status_changed_at),
            noOfDays: WITH_CONCIERGE.number_of_days,
            lastCaseComment: WITH_CONCIERGE.last_comment
          },
          {
            oldestOrder: orderPlaced,
            orderId: WITH_CONCIERGE.order_id,
            timestamp: getFormattedDate(WITH_CONCIERGE.status_changed_at),
            noOfDays: WITH_CONCIERGE.number_of_days,
            lastCaseComment: WITH_CONCIERGE.last_comment
          },
          {
            oldestOrder: orderActivated,
            orderId: WITH_CONCIERGE.order_id,
            timestamp: getFormattedDate(WITH_CONCIERGE.status_changed_at),
            noOfDays: WITH_CONCIERGE.number_of_days,
            lastCaseComment: WITH_CONCIERGE.last_comment
          },
          {
            oldestOrder: orderShipped,
            orderId: WITH_CONCIERGE.order_id,
            timestamp: getFormattedDate(WITH_CONCIERGE.status_changed_at),
            noOfDays: WITH_CONCIERGE.number_of_days,
            lastCaseComment: WITH_CONCIERGE.last_comment
          },
          {
            oldestOrder: orderCompleted,
            orderId: WITH_CONCIERGE.order_id,
            timestamp: getFormattedDate(WITH_CONCIERGE.status_changed_at),
            noOfDays: WITH_CONCIERGE.number_of_days,
            lastCaseComment: WITH_CONCIERGE.last_comment
          },
          {
            oldestOrder: contractExpired,
            orderId: CONTRACT_EXPIRED.order_id,
            timestamp: getFormattedDate(CONTRACT_EXPIRED.status_changed_at),
            noOfDays: CONTRACT_EXPIRED.number_of_days,
            lastCaseComment: CONTRACT_EXPIRED.last_comment
          }
        ];
        const agent = options.find(item => item.id === userId);
        const option = {
          label: agent.label,
          id: userId
        };

        setStatsBarChart(newStatsBarChart);
        setStatsTable(newStatsTable);
        setSelectedOption(option);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    const getUsers = async () => {
      const result = await request.get('/sovAgentAndAdminUsers');
      if (result) {
        const transformedResult = _.get(result, 'data', []).map(item => ({
          label: item.name,
          id: item.id
        }));
        setOptions(transformedResult);
      }
    };
    getUsers();
  }, []);

  useEffect(() => {
    // if (!agents) return;
    // const newOptions = options.concat(
    //   agents.map(agent => {
    //     return {
    //       label: agent.label,
    //       id: agent.id
    //     };
    //   })
    // );
    // setOptions(newOptions);
  }, []);

  const onChange = selected => {
    getStats(selected && selected.length && selected[0].id);
  };

  return (
    <ExpandCollapse initialOpen={[]} topDivider={false}>
      {(expandProps) => (
        <ExpandCollapse.Panel
          {...expandProps}
          panelId="filters"
          control={<Typography variant={{ bold: true }}>{analytics}</Typography>}
        >
          <Flex>
            <Box width="55%">
              <TypeAhead
                label={selectTheView}
                placeholder=""
                options={options}
                selected={selectedOption ? [selectedOption] : []}
                onChange={onChange}
              />
              {statsTable && (
                <>
                  <hr />
                  <Table control={tableHeader} data={statsTable} />
                </>
              )}
            </Box>
            <Box width="35%">
              {isLoading ? (
                <Typography heading="h2" variant={{ size: 'h2' }}>{loading}</Typography>
              ) : (
                <>
                  {statsBarChart && (
                    <BarChart
                      stats={statsBarChart}
                      totalCount={totalCount}
                      bars={bars}
                    />
                  )}
                </>
              )}
            </Box>
          </Flex>
        </ExpandCollapse.Panel>
      )}
    </ExpandCollapse>
  );
};

export default Analytics;
